/**
 * Class schedules are authored in the teacher's timezone (IST for every
 * current teacher). A seeker in London or New York cannot tell from
 * "07:00 IST" whether a class is realistic for them, so these helpers convert
 * each slot into the viewer's own timezone — including the day shift, which is
 * what actually trips people up ("Mon 07:00 IST" is Sunday evening in the US).
 */
import { formatDayList, normalizeDayToken, parseScheduleJson } from "./scheduleDisplay.js";

export const SOURCE_TIME_ZONE = "Asia/Kolkata";
export const SOURCE_TIME_ZONE_LABEL = "IST";

const DEFAULT_SOURCE_TIME_ZONE = SOURCE_TIME_ZONE;

// Schedules store an abbreviation, not an IANA id.
const SOURCE_ZONE_BY_ABBREVIATION = {
  IST: "Asia/Kolkata",
  GMT: "Etc/GMT",
  UTC: "Etc/UTC",
};

const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/**
 * Returns "" during prerendering so the static HTML keeps the teacher's own
 * IST schedule rather than freezing the build machine's timezone into pages
 * served to everyone.
 */
export function detectViewerTimeZone() {
  if (typeof navigator !== "undefined" && /ReactSnap/i.test(navigator.userAgent || "")) {
    return "";
  }
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function sourceTimeZone(entry) {
  const key = String(entry?.timezone || "").trim().toUpperCase();
  return SOURCE_ZONE_BY_ABBREVIATION[key] || DEFAULT_SOURCE_TIME_ZONE;
}

function zoneParts(date, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = {};
  for (const part of formatter.formatToParts(date)) parts[part.type] = part.value;
  return parts;
}

/** How far the zone is from UTC at this instant, in milliseconds. */
function zoneOffsetMs(date, timeZone) {
  const parts = zoneParts(date, timeZone);
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second)
  );
  return asUtc - date.getTime();
}

/** The instant at which a wall-clock time occurs in a given zone. */
function instantFromZonedTime(year, month, day, hour, minute, timeZone) {
  const guess = Date.UTC(year, month, day, hour, minute);
  const firstPass = new Date(guess - zoneOffsetMs(new Date(guess), timeZone));
  // A second pass settles instants that sit next to a DST transition.
  return new Date(guess - zoneOffsetMs(firstPass, timeZone));
}

function nextOccurrence(weekdayIndex, hour, minute, timeZone, from) {
  const parts = zoneParts(from, timeZone);
  const year = Number(parts.year);
  const month = Number(parts.month) - 1;
  const day = Number(parts.day);
  const currentWeekday = new Date(Date.UTC(year, month, day)).getUTCDay();
  const daysAhead = (weekdayIndex - currentWeekday + 7) % 7;
  return instantFromZonedTime(year, month, day + daysAhead, hour, minute, timeZone);
}

function formatInZone(instant, timeZone) {
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  const parts = {};
  for (const part of formatter.formatToParts(instant)) parts[part.type] = part.value;
  const hour24 = Number(zoneParts(instant, timeZone).hour) % 24;
  return {
    weekday: parts.weekday || "",
    time: `${parts.hour}:${parts.minute} ${parts.dayPeriod}`.trim(),
    hour: Number.isFinite(hour24) ? hour24 : null,
  };
}

function parseClock(value) {
  const match = String(value || "").trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return null;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (!Number.isFinite(hour) || !Number.isFinite(minute) || hour > 23 || minute > 59) return null;
  return { hour, minute };
}

function parseTimeRange(value) {
  const raw = String(value || "").trim();
  const range = raw.match(/^(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
  if (range) return { start: parseClock(range[1]), end: parseClock(range[2]) };
  return { start: parseClock(raw), end: null };
}

/**
 * Convert one schedule entry into the viewer's timezone.
 * Days are grouped by resulting local time, so a slot that lands on a
 * different local time for part of the week still reads correctly.
 */
export function localizeScheduleEntry(entry, viewerTimeZone, now = new Date()) {
  if (!entry || !viewerTimeZone) return [];

  const { start, end } = parseTimeRange(entry.time);
  if (!start) return [];

  const dayTokens = (Array.isArray(entry.days) ? entry.days : [])
    .map(normalizeDayToken)
    .filter((day) => day in WEEKDAY_INDEX);
  if (!dayTokens.length) return [];

  const fromZone = sourceTimeZone(entry);
  const groupedByTime = new Map();

  for (const token of dayTokens) {
    const startInstant = nextOccurrence(
      WEEKDAY_INDEX[token],
      start.hour,
      start.minute,
      fromZone,
      now
    );
    const local = formatInZone(startInstant, viewerTimeZone);

    let time = local.time;
    if (end) {
      const endInstant = new Date(
        startInstant.getTime() +
          ((end.hour * 60 + end.minute - (start.hour * 60 + start.minute) + 1440) % 1440) * 60000
      );
      time = `${local.time} – ${formatInZone(endInstant, viewerTimeZone).time}`;
    }

    if (!groupedByTime.has(time)) groupedByTime.set(time, { days: [], hour: local.hour });
    groupedByTime.get(time).days.push(local.weekday);
  }

  return [...groupedByTime.entries()].map(([time, group]) => ({
    days: formatDayList(group.days),
    time,
    hour: group.hour,
    period: timePeriodForHour(group.hour),
  }));
}

/** Flatten a teacher's offerings into de-duplicated local class times. */
export function localizeScheduleEntries(entries, viewerTimeZone, now = new Date()) {
  if (!viewerTimeZone) return [];

  const seen = new Set();
  const slots = [];

  for (const entry of entries || []) {
    for (const slot of localizeScheduleEntry(entry, viewerTimeZone, now)) {
      const key = `${slot.days}|${slot.time}`;
      if (seen.has(key)) continue;
      seen.add(key);
      slots.push(slot);
    }
  }

  return slots;
}

/** Raw schedule entries for one teacher, de-duplicated across offerings. */
export function collectScheduleEntries(offerings) {
  const seen = new Set();
  const entries = [];

  for (const offering of offerings || []) {
    for (const entry of parseScheduleJson(offering?.schedule ?? offering?.schedule_json)) {
      const days = Array.isArray(entry?.days) ? entry.days.map(normalizeDayToken) : [];
      const time = String(entry?.time || "").trim();
      if (!days.length || !time) continue;

      const key = `${days.join(",")}|${time}|${entry.timezone || ""}`;
      if (seen.has(key)) continue;
      seen.add(key);
      entries.push({ days, time, timezone: entry.timezone || "IST" });
    }
  }

  return entries;
}

export const TIME_PERIOD_CHIPS = [
  { id: "morning", label: "Morning where I am" },
  { id: "afternoon", label: "Afternoon where I am" },
  { id: "evening", label: "Evening where I am" },
];

/** Morning 5–12, afternoon 12–17, evening 17–22, otherwise late-night. */
export function timePeriodForHour(hour) {
  if (!Number.isFinite(hour)) return "";
  if (hour >= 5 && hour < 12) return "morning";
  if (hour >= 12 && hour < 17) return "afternoon";
  if (hour >= 17 && hour < 22) return "evening";
  return "late";
}

export function teacherMatchesTimePeriod(slots, periodId) {
  if (!periodId) return true;
  return (slots || []).some((slot) => slot.period === periodId);
}

/** Short, human label for a timezone: "Kolkata · GMT+5:30". */
export function describeTimeZone(timeZone) {
  if (!timeZone) return "";

  const city = timeZone.split("/").pop().replace(/_/g, " ");
  let offset = "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    offset = parts.find((part) => part.type === "timeZoneName")?.value || "";
  } catch {
    offset = "";
  }

  return offset ? `${city} · ${offset}` : city;
}
